/**
 * Client-Safe RBAC Permission Definitions
 *
 * This file contains only the raw permission data without any better-auth imports.
 * It's safe to use in client-side code and doesn't pull in Node.js crypto.
 *
 * For server-side code, use permissions.ts which wraps these in createAccessControl.
 */

// Minimal role type matching better-auth's role structure for client-side use
type RoleObject = {
  authorize: (
    request: Record<string, string[] | { connector: "AND" | "OR"; actions: string[] }>,
    connector?: "AND" | "OR",
  ) => { success: boolean; error?: string }
  statements: Record<string, readonly string[]>
}

// Minimal access control type matching better-auth's structure for client-side use
type AccessControl<S extends Record<string, readonly string[]>> = {
  newRole: (statements: Record<string, string[]>) => RoleObject
  statements: S
}

/**
 * Client-safe role factory matching better-auth's role() function.
 * Used for client-side permission checking without pulling in Node.js crypto.
 */
function createRole(statements: Record<string, readonly string[]>): RoleObject {
  return {
    authorize(request, connector = "AND") {
      let success = false
      for (const [requestedResource, requestedActions] of Object.entries(request)) {
        const allowedActions = statements[requestedResource]
        if (!allowedActions) {
          return {
            success: false,
            error: `You are not allowed to access resource: ${requestedResource}`,
          }
        }
        if (Array.isArray(requestedActions)) {
          success = requestedActions.every((action) => allowedActions.includes(action))
        } else if (typeof requestedActions === "object") {
          const actions = requestedActions
          if (actions.connector === "OR") {
            success = actions.actions.some((action) => allowedActions.includes(action))
          } else {
            success = actions.actions.every((action) => allowedActions.includes(action))
          }
        }
        if (success && connector === "OR") return { success }
        if (!success && connector === "AND") {
          return {
            success: false,
            error: `unauthorized to access resource "${requestedResource}"`,
          }
        }
      }
      return success ? { success } : { success: false, error: "Not authorized" }
    },
    statements,
  }
}

/**
 * Client-safe createAccessControl matching better-auth's function.
 * Used for client-side permission checking without pulling in Node.js crypto.
 */
function createClientAccessControl<S extends Record<string, readonly string[]>>(
  s: S,
): AccessControl<S> {
  return {
    newRole(stmts) {
      return createRole(stmts as Record<string, readonly string[]>)
    },
    statements: s,
  }
}

/**
 * Statement object defining all resources and their allowed actions.
 * This is the foundation of the permission system.
 */
export const statement = {
  project: [
    "create",
    "read",
    "update",
    "delete",
    "hardDelete",
    "bulkUpdate",
    "bulkExport",
    "bulkImport",
    "approve",
  ],
  store: ["create", "read", "update", "bulkUpdate"],
  storeVersion: ["create", "read", "update", "delete"],
  configVersion: ["create", "read", "update", "delete"],
  merchandiseGroup: ["read"],
  auditLog: ["read"],
  fixtureValidation: ["read"],
  /**
   * Bulk updates. `review` is the review step (check, approve, hand-tune a
   * blueprint); `manage` is everything else in the wizard; `publish` writes
   * the approved stores out as new blueprints, which cannot be undone.
   */
  changeProject: ["review", "manage", "publish"],
  user: ["create", "read", "update", "delete", "set-role", "set-access"],
  file: ["create", "read", "update", "delete", "share"],
  invitation: ["create", "read", "revoke"],
  featureFlag: ["create", "read", "update", "delete", "archive"],
  countryCodeMapping: ["create", "read", "update", "delete"],
  storeSizeBucket: ["read", "update"],
  standardStore: ["read", "update"],
  shelfTypeOrder: ["read", "update"],
} as const

/**
 * Role statements - plain objects defining what each role can do.
 * These are converted to ac.newRole() in permissions.ts for server use.
 */
export const roleStatements = {
  admin: {
    project: [
      "create",
      "read",
      "update",
      "delete",
      "hardDelete",
      "bulkUpdate",
      "bulkExport",
      "bulkImport",
      "approve",
    ],
    store: ["create", "read", "update", "bulkUpdate"],
    storeVersion: ["create", "read", "update", "delete"],
    configVersion: ["create", "read", "update", "delete"],
    merchandiseGroup: ["read"],
    auditLog: ["read"],
    fixtureValidation: ["read"],
    changeProject: ["review", "manage", "publish"],
    user: ["create", "read", "update", "delete", "set-role", "set-access"],
    file: ["create", "read", "update", "delete", "share"],
    invitation: ["create", "read", "revoke"],
    featureFlag: ["create", "read", "update", "delete", "archive"],
    countryCodeMapping: ["create", "read", "update", "delete"],
    storeSizeBucket: ["read", "update"],
    standardStore: ["read", "update"],
    shelfTypeOrder: ["read", "update"],
  },
  approval_manager: {
    project: [
      "create",
      "read",
      "update",
      "delete",
      "bulkUpdate",
      "bulkExport",
      "bulkImport",
      "approve",
    ],
    store: ["create", "read", "update", "bulkUpdate"],
    storeVersion: ["create", "read", "update", "delete"],
    configVersion: ["create", "read", "update", "delete"],
    merchandiseGroup: ["read"],
    auditLog: ["read"],
    fixtureValidation: ["read"],
    changeProject: ["review", "manage"],
    file: ["create", "read", "update", "delete", "share"],
    invitation: ["create", "read", "revoke"],
    countryCodeMapping: ["create", "read", "update", "delete"],
    storeSizeBucket: ["read", "update"],
    standardStore: ["read", "update"],
    shelfTypeOrder: ["read", "update"],
  },
  blueprint_planner: {
    project: ["create", "read", "update", "delete", "bulkUpdate", "bulkExport", "bulkImport"],
    store: ["create", "read", "update", "bulkUpdate"],
    storeVersion: ["create", "read", "update", "delete"],
    configVersion: ["create", "read", "update", "delete"],
    merchandiseGroup: ["read"],
    auditLog: ["read"],
    fixtureValidation: ["read"],
    changeProject: ["review"],
    file: ["create", "read", "update", "delete", "share"],
    invitation: ["create", "read", "revoke"],
    countryCodeMapping: ["create", "read", "update", "delete"],
    storeSizeBucket: ["read", "update"],
    standardStore: ["read", "update"],
    shelfTypeOrder: ["read", "update"],
  },
  /**
   * Helps review bulk updates: sees the review step only, may check, approve
   * and hand-tune blueprints there. Read-only everywhere else.
   */
  blueprint_reviewer: {
    project: ["read"],
    store: ["read"],
    storeVersion: ["read"],
    configVersion: ["read"],
    merchandiseGroup: ["read"],
    changeProject: ["review"],
    file: ["read"],
    countryCodeMapping: ["read"],
    storeSizeBucket: ["read"],
    standardStore: ["read"],
    shelfTypeOrder: ["read"],
  },
  store_designer: {
    project: ["read"],
    store: ["read", "update"],
    storeVersion: ["create", "read", "update", "delete"],
    configVersion: ["read"],
    merchandiseGroup: ["read"],
    file: ["read", "share"],
    invitation: ["create", "read", "revoke"],
    countryCodeMapping: ["read"],
    storeSizeBucket: ["read"],
    standardStore: ["read"],
    shelfTypeOrder: ["read"],
  },
  store_manager: {
    project: ["read"],
    store: ["read"],
    storeVersion: ["read"],
    configVersion: ["read"],
    merchandiseGroup: ["read"],
    file: ["read", "create", "share"],
    invitation: ["create", "read", "revoke"],
    countryCodeMapping: ["read"],
    storeSizeBucket: ["read"],
    standardStore: ["read"],
    shelfTypeOrder: ["read"],
  },
  external_viewer: {
    project: ["read"],
    store: ["read"],
    storeVersion: ["read"],
    file: ["read"],
    countryCodeMapping: ["read"],
    storeSizeBucket: ["read"],
    standardStore: ["read"],
    shelfTypeOrder: ["read"],
  },
} as const

/**
 * Role type for TypeScript type safety
 */
export type Role = keyof typeof roleStatements

/**
 * All valid role names
 */
export const roleNames = Object.keys(roleStatements) as Role[]

/**
 * Role hierarchy for determining highest privilege when a user has multiple Azure AD roles.
 * Higher index = higher privilege.
 */
export const roleHierarchy: Role[] = [
  "external_viewer",
  "store_manager",
  "store_designer",
  "blueprint_reviewer",
  "blueprint_planner",
  "approval_manager",
  "admin",
]

/**
 * Get the highest privilege role from a list of roles.
 * Used when Azure AD returns multiple application roles.
 */
export function getHighestPrivilegeRole(userRoles: string[]): Role | null {
  let highestRole: Role | null = null
  let highestIndex = -1

  for (const role of userRoles) {
    const index = roleHierarchy.indexOf(role as Role)
    if (index > highestIndex) {
      highestIndex = index
      highestRole = role as Role
    }
  }

  return highestRole
}

/**
 * Resource types for type-safe permission checking
 */
export type Resource = keyof typeof statement

/**
 * Get all actions available for a resource
 */
export type ActionsForResource<R extends Resource> = (typeof statement)[R][number]

/** Whether a role may perform an action, straight from the role statements. */
export function roleHasPermission<R extends Resource>(
  role: string | null | undefined,
  resource: R,
  action: ActionsForResource<R>,
): boolean {
  if (!role || !(role in roleStatements)) return false
  const allowed: readonly string[] | undefined =
    roleStatements[role as Role][resource as keyof (typeof roleStatements)[Role]]
  return allowed?.includes(action) ?? false
}

/**
 * Client-safe access control instance.
 * Used by adminClient for client-side permission checks.
 */
export const ac = createClientAccessControl(statement)

/**
 * Helper to convert readonly role statements to the format expected by newRole.
 */
function toRoleStatements(stmts: Record<string, readonly string[]>): Record<string, string[]> {
  return Object.fromEntries(Object.entries(stmts).map(([k, v]) => [k, [...v]]))
}

/**
 * Client-safe roles for use with adminClient.
 * These match the structure expected by better-auth's adminClient plugin.
 */
export const roles = {
  admin: ac.newRole(toRoleStatements(roleStatements.admin)),
  approval_manager: ac.newRole(toRoleStatements(roleStatements.approval_manager)),
  blueprint_planner: ac.newRole(toRoleStatements(roleStatements.blueprint_planner)),
  blueprint_reviewer: ac.newRole(toRoleStatements(roleStatements.blueprint_reviewer)),
  store_designer: ac.newRole(toRoleStatements(roleStatements.store_designer)),
  store_manager: ac.newRole(toRoleStatements(roleStatements.store_manager)),
  external_viewer: ac.newRole(toRoleStatements(roleStatements.external_viewer)),
}
